import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cursoService from '../services/CursoService';
import materiaService from '../services/MateriaService';
import divisionService from '../services/DivisionService';
import procesosAsincronicosService from '../services/ProcesosAsincronicosService';
import TableExportActions from './common/TableExportActions';
import EvaluacionesCursoModal from './evaluaciones/EvaluacionesCursoModal';
import EstadoAcademicoChip from './evaluaciones/EstadoAcademicoChip';
import AlumnoNotasDetalle from './evaluaciones/AlumnoNotasDetalle';
import useResumenAcademico from './evaluaciones/useResumenAcademico';
import { crearEvaluacionesApiColegio } from '../services/EvaluacionesApi';
import { ITEMS_PER_PAGE_OPTIONS } from '../utils/pagination';

const DEFAULT_ITEMS_PER_PAGE = 300;

const getRange = (page, pageSize = DEFAULT_ITEMS_PER_PAGE) => {
  const safePageSize = Math.max(Number(pageSize) || DEFAULT_ITEMS_PER_PAGE, 1);
  const start = Math.max(page - 1, 0) * safePageSize;
  return { start, end: start + safePageSize - 1 };
};

const renderPagination = (totalItems, page, setPage, pageSize, setPageSize) => {
  if (totalItems <= 0) return null;
  const safePageSize = Math.max(Number(pageSize) || DEFAULT_ITEMS_PER_PAGE, 1);
  const totalPages = Math.max(1, Math.ceil(totalItems / safePageSize));
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  const maxVisiblePages = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage < maxVisiblePages - 1) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  const buttons = [];

  if (startPage > 1 && totalPages > 1) {
    buttons.push(
      <button key="first" type="button" className="pagination-btn" onClick={() => setPage(1)}>
        1
      </button>
    );
    if (startPage > 2) {
      buttons.push(<span key="ellipsis-start" className="pagination-ellipsis">...</span>);
    }
  }

  if (totalPages > 1) {
    for (let nextPage = startPage; nextPage <= endPage; nextPage += 1) {
      buttons.push(
        <button
          key={nextPage}
          type="button"
          className={`pagination-btn ${nextPage === currentPage ? 'active' : ''}`}
          onClick={() => setPage(nextPage)}
        >
          {nextPage}
        </button>
      );
    }
  } else {
    buttons.push(
      <button key="single" type="button" className="pagination-btn active" disabled>
        1
      </button>
    );
  }

  if (endPage < totalPages && totalPages > 1) {
    if (endPage < totalPages - 1) {
      buttons.push(<span key="ellipsis-end" className="pagination-ellipsis">...</span>);
    }
    buttons.push(
      <button key="last" type="button" className="pagination-btn" onClick={() => setPage(totalPages)}>
        {totalPages}
      </button>
    );
  }

  const startItem = (currentPage - 1) * safePageSize + 1;
  const endItem = Math.min(currentPage * safePageSize, totalItems);

  return (
    <div className="pagination-container">
      <div className="items-per-page-container">
        <label htmlFor={`items-per-page-cursos-${String(totalItems)}-${String(page)}`}>Mostrar</label>
        <select
          id={`items-per-page-cursos-${String(totalItems)}-${String(page)}`}
          className="items-per-page-select"
          value={safePageSize}
          onChange={(e) => {
            const nextPageSize = Number(e.target.value) || DEFAULT_ITEMS_PER_PAGE;
            setPageSize(nextPageSize);
            setPage(1);
          }}
        >
          {ITEMS_PER_PAGE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <span>registros</span>
      </div>
      <div className="pagination-info">
        Mostrando {startItem}-{endItem} de {totalItems}
      </div>
      <div className="pagination-controls">
        <button
          type="button"
          className="pagination-btn nav-btn"
          onClick={() => setPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1 || totalPages === 1}
          aria-label="Pagina anterior"
        >
          <i className="fas fa-chevron-left" />
        </button>
        {buttons}
        <button
          type="button"
          className="pagination-btn nav-btn"
          onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages || totalPages === 1}
          aria-label="Pagina siguiente"
        >
          <i className="fas fa-chevron-right" />
        </button>
      </div>
    </div>
  );
};

const getCursoValue = (curso, key) => curso?.[key] ?? curso?.[key.charAt(0).toUpperCase() + key.slice(1)];

const normalizeFilterValue = (value) => String(value || '').trim().toLowerCase();

const formatCourseLabel = (curso) => {
  const anio = getCursoValue(curso, 'valorAnio');
  const division = getCursoValue(curso, 'division');
  if (anio && division) {
    return `${anio}° ${division}`;
  }

  return getCursoValue(curso, 'nombre') || 'Curso';
};

const formatCourseFullLabel = (curso) => getCursoValue(curso, 'nombre') || formatCourseLabel(curso);

const normalizeConfirmText = (value) => String(value || '').trim().replace(/\s+/g, ' ').toUpperCase();

const AdminCursosSection = ({ activeColegioId }) => {
  const navigate = useNavigate();
  const [cursos, setCursos] = useState([]);
  const [cursosLoading, setCursosLoading] = useState(false);
  const [cursosError, setCursosError] = useState('');
  const [cursosNotice, setCursosNotice] = useState('');
  const [cursosSearch, setCursosSearch] = useState('');
  const [cursosPage, setCursosPage] = useState(1);
  const [cursosTotal, setCursosTotal] = useState(0);
  const [cursosPageSize, setCursosPageSize] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [cicloFilter, setCicloFilter] = useState('');
  const [anioFilter, setAnioFilter] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('');
  const [aniosCatalogo, setAniosCatalogo] = useState([]);
  const [divisionesCatalogo, setDivisionesCatalogo] = useState([]);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [importAlumnosModalOpen, setImportAlumnosModalOpen] = useState(false);
  const [selectedCursoForImport, setSelectedCursoForImport] = useState(null);
  const [importAlumnosFile, setImportAlumnosFile] = useState(null);
  const [importAlumnosLoading, setImportAlumnosLoading] = useState(false);
  const [importAlumnosMessage, setImportAlumnosMessage] = useState('');
  const [alumnosModalOpen, setAlumnosModalOpen] = useState(false);
  const [selectedCursoForAlumnos, setSelectedCursoForAlumnos] = useState(null);
  const [cursoAlumnos, setCursoAlumnos] = useState([]);
  const [cursoAlumnosLoading, setCursoAlumnosLoading] = useState(false);
  const [cursoAlumnosError, setCursoAlumnosError] = useState('');
  const [cursoAlumnosNotice, setCursoAlumnosNotice] = useState('');
  const [cursoAlumnosSearch, setCursoAlumnosSearch] = useState('');
  // Evaluaciones (por materia): curso abierto en el modal de evaluaciones y detalle de notas de un alumno.
  const [evaluacionesCurso, setEvaluacionesCurso] = useState(null);
  const [alumnoNotasDetalle, setAlumnoNotasDetalle] = useState(null);
  const evaluacionesCursoId = evaluacionesCurso ? String(evaluacionesCurso.curso?.id || evaluacionesCurso.curso?.Id || '') : '';
  const evaluacionesApi = useMemo(
    () => (evaluacionesCursoId ? crearEvaluacionesApiColegio({ idCurso: evaluacionesCursoId }) : null),
    [evaluacionesCursoId]
  );
  const resumenCursoId = alumnosModalOpen && selectedCursoForAlumnos
    ? String(selectedCursoForAlumnos.id || selectedCursoForAlumnos.Id || '')
    : '';
  const resumenAcademicoApi = useMemo(
    () => (resumenCursoId ? crearEvaluacionesApiColegio({ idCurso: resumenCursoId }) : null),
    [resumenCursoId]
  );
  const resumenAcademico = useResumenAcademico(resumenAcademicoApi, Boolean(resumenAcademicoApi));
  const [profesoresModalOpen, setProfesoresModalOpen] = useState(false);
  const [selectedCursoForProfesores, setSelectedCursoForProfesores] = useState(null);
  const [cursoProfesores, setCursoProfesores] = useState([]);
  const [cursoProfesoresLoading, setCursoProfesoresLoading] = useState(false);
  const [cursoProfesoresError, setCursoProfesoresError] = useState('');
  const [cursoProfesoresSearch, setCursoProfesoresSearch] = useState('');
  const [removingAlumnoIds, setRemovingAlumnoIds] = useState([]);
  const [materiasModalOpen, setMateriasModalOpen] = useState(false);
  const [selectedCursoForMaterias, setSelectedCursoForMaterias] = useState(null);
  const [cursoMaterias, setCursoMaterias] = useState([]);
  const [cursoMateriasLoading, setCursoMateriasLoading] = useState(false);
  const [cursoMateriasError, setCursoMateriasError] = useState('');
  const [cursoMateriasNotice, setCursoMateriasNotice] = useState('');
  const [removingMateriaIds, setRemovingMateriaIds] = useState([]);
  const cursoAlumnosRequestRef = useRef(0);
  const cursoProfesoresRequestRef = useRef(0);
  const cursoMateriasRequestRef = useRef(0);
  const [deleteCursoModal, setDeleteCursoModal] = useState({
    open: false,
    curso: null,
    confirmText: '',
    loading: false,
    error: ''
  });
  const [removeAllAlumnosModal, setRemoveAllAlumnosModal] = useState({
    open: false,
    confirmText: '',
    loading: false,
    error: ''
  });
  const [removeAllMateriasModal, setRemoveAllMateriasModal] = useState({
    open: false,
    confirmText: '',
    loading: false,
    error: ''
  });

  const templateUrl = `${process.env.PUBLIC_URL || ''}/templates/plantilla-importacion-cursos.xlsx`;
  const alumnosTemplateUrl = `${process.env.PUBLIC_URL || ''}/templates/plantilla-importacion-alumnos-curso.xlsx`;

  const loadCursos = useCallback(async () => {
    if (!activeColegioId) return;

    setCursosLoading(true);
    setCursosError('');
    try {
      const { start, end } = getRange(cursosPage, cursosPageSize);
      const response = await cursoService.getCursos({
        idColegio: activeColegioId,
        start,
        end,
        includeTotal: true
      });
      const items = Array.isArray(response?.items) ? response.items : Array.isArray(response) ? response : [];
      setCursos(items);
      setCursosTotal(response?.total ?? items.length);
    } catch (error) {
      setCursosError(error.message || 'No pudimos cargar los cursos.');
      setCursos([]);
      setCursosTotal(0);
    } finally {
      setCursosLoading(false);
    }
  }, [activeColegioId, cursosPage, cursosPageSize]);

  useEffect(() => {
    loadCursos();
  }, [loadCursos]);

  useEffect(() => {
    const loadCatalogos = async () => {
      try {
        const [aniosResponse, divisionesResponse] = await Promise.all([
          materiaService.getAniosActivos(),
          activeColegioId ? divisionService.getDivisionesActivas(activeColegioId) : Promise.resolve([])
        ]);

        setAniosCatalogo(Array.isArray(aniosResponse) ? aniosResponse : []);
        setDivisionesCatalogo(Array.isArray(divisionesResponse) ? divisionesResponse : []);
      } catch (error) {
        console.warn('No pudimos cargar años o divisiones para filtros de cursos:', error);
      }
    };

    loadCatalogos();
  }, [activeColegioId]);

  useEffect(() => {
    setCursosPage(1);
  }, [cursosSearch, cicloFilter, anioFilter, divisionFilter]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(cursosTotal / Math.max(cursosPageSize, 1)));
    if (cursosPage > totalPages) {
      setCursosPage(totalPages);
    }
  }, [cursosPage, cursosPageSize, cursosTotal]);

  const cicloOptions = useMemo(() => (
    [...new Set(cursos.map((curso) => String(getCursoValue(curso, 'cicloLectivo') || '')).filter(Boolean))]
      .sort((a, b) => Number(b) - Number(a))
  ), [cursos]);

  const anioOptions = useMemo(() => (
    [...new Set(
      (aniosCatalogo.length > 0
        ? aniosCatalogo.map((anio) => String(anio.valor || anio.Valor || ''))
        : cursos.map((curso) => String(getCursoValue(curso, 'valorAnio') || '')))
        .filter(Boolean)
    )]
      .sort((a, b) => Number(a) - Number(b))
  ), [aniosCatalogo, cursos]);

  const divisionOptions = useMemo(() => (
    [...new Set(
      (divisionesCatalogo.length > 0
        ? divisionesCatalogo.map((division) => String(division.nombre || division.Nombre || '').trim())
        : cursos.map((curso) => String(getCursoValue(curso, 'division') || '').trim()))
        .filter(Boolean)
    )]
      .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
  ), [cursos, divisionesCatalogo]);

  const filteredCursos = useMemo(() => {
    const term = normalizeFilterValue(cursosSearch);
    const normalizedDivisionFilter = normalizeFilterValue(divisionFilter);

    return cursos.filter((curso) => {
      const nombre = normalizeFilterValue(getCursoValue(curso, 'nombre'));
      const division = normalizeFilterValue(getCursoValue(curso, 'division'));
      const colegio = normalizeFilterValue(getCursoValue(curso, 'nombreColegio'));
      const ciclo = String(getCursoValue(curso, 'cicloLectivo') || '').trim();
      const anio = String(getCursoValue(curso, 'valorAnio') || '').trim();
      const matchesSearch = !term || [nombre, division, colegio, ciclo, anio, formatCourseLabel(curso).toLowerCase()].some((value) => value.includes(term));
      const matchesCiclo = !cicloFilter || ciclo === cicloFilter;
      const matchesAnio = !anioFilter || anio === anioFilter;
      const matchesDivision = !normalizedDivisionFilter || division === normalizedDivisionFilter;

      return matchesSearch && matchesCiclo && matchesAnio && matchesDivision;
    });
  }, [anioFilter, cicloFilter, cursos, cursosSearch, divisionFilter]);

  const cursosVisibleStats = useMemo(() => {
    const ciclos = new Set();
    const anios = new Set();
    const divisiones = new Set();
    const profesores = new Set();

    filteredCursos.forEach((curso) => {
      const ciclo = getCursoValue(curso, 'cicloLectivo');
      const anio = getCursoValue(curso, 'valorAnio');
      const division = getCursoValue(curso, 'division');
      const profesorIds = getCursoValue(curso, 'profesorIds');
      if (ciclo) ciclos.add(ciclo);
      if (anio) anios.add(anio);
      if (division) divisiones.add(division);
      if (Array.isArray(profesorIds)) {
        profesorIds.forEach((profesorId) => {
          if (profesorId) {
            profesores.add(String(profesorId));
          }
        });
      }
    });

    return {
      ciclos: ciclos.size,
      anios: anios.size,
      divisiones: divisiones.size,
      profesores: profesores.size
    };
  }, [filteredCursos]);

  const openImportModal = () => {
    setCursosNotice('');
    setImportModalOpen(true);
    setImportFile(null);
    setImportMessage('');
  };

  const closeImportModal = () => {
    if (importLoading) return;
    setImportModalOpen(false);
    setImportFile(null);
    setImportMessage('');
  };

  const openImportAlumnosModal = (curso) => {
    setCursosNotice('');
    setSelectedCursoForImport(curso);
    setImportAlumnosModalOpen(true);
    setImportAlumnosFile(null);
    setImportAlumnosMessage('');
  };

  const closeImportAlumnosModal = () => {
    if (importAlumnosLoading) return;
    setImportAlumnosModalOpen(false);
    setSelectedCursoForImport(null);
    setImportAlumnosFile(null);
    setImportAlumnosMessage('');
  };

  const syncCursoAlumnoCount = useCallback((cursoId, nextCount) => {
    setCursos((prevCursos) => prevCursos.map((curso) => {
      const currentCursoId = getCursoValue(curso, 'id');
      if (currentCursoId !== cursoId) {
        return curso;
      }

      return {
        ...curso,
        cantidadAlumnos: nextCount,
        CantidadAlumnos: nextCount
      };
    }));

    setSelectedCursoForAlumnos((prevCurso) => {
      if (!prevCurso || getCursoValue(prevCurso, 'id') !== cursoId) {
        return prevCurso;
      }

      return {
        ...prevCurso,
        cantidadAlumnos: nextCount,
        CantidadAlumnos: nextCount
      };
    });
  }, []);

  const syncCursoMateriaCount = useCallback((cursoId, nextCount) => {
    setCursos((prevCursos) => prevCursos.map((curso) => {
      const currentCursoId = getCursoValue(curso, 'id');
      if (currentCursoId !== cursoId) {
        return curso;
      }

      return {
        ...curso,
        cantidadMaterias: nextCount,
        CantidadMaterias: nextCount
      };
    }));

    setSelectedCursoForMaterias((prevCurso) => {
      if (!prevCurso || getCursoValue(prevCurso, 'id') !== cursoId) {
        return prevCurso;
      }

      return {
        ...prevCurso,
        cantidadMaterias: nextCount,
        CantidadMaterias: nextCount
      };
    });
  }, []);

  const loadCursoAlumnos = useCallback(async (curso) => {
    const cursoId = getCursoValue(curso, 'id');
    const requestId = cursoAlumnosRequestRef.current + 1;
    cursoAlumnosRequestRef.current = requestId;
    if (!cursoId) {
      setCursoAlumnos([]);
      setCursoAlumnosError('No pudimos identificar el curso seleccionado.');
      setCursoAlumnosLoading(false);
      return;
    }

    setCursoAlumnosLoading(true);
    setCursoAlumnosError('');
    setCursoAlumnosNotice('');
    try {
      const alumnos = await cursoService.getAlumnosPorCurso(cursoId);
      if (cursoAlumnosRequestRef.current !== requestId) {
        return;
      }
      setCursoAlumnos(Array.isArray(alumnos) ? alumnos : []);
    } catch (error) {
      if (cursoAlumnosRequestRef.current !== requestId) {
        return;
      }
      setCursoAlumnos([]);
      setCursoAlumnosError(error.message || 'No pudimos cargar los alumnos del curso.');
    } finally {
      if (cursoAlumnosRequestRef.current === requestId) {
        setCursoAlumnosLoading(false);
      }
    }
  }, []);

  const loadCursoProfesores = useCallback(async (curso) => {
    const cursoId = getCursoValue(curso, 'id');
    const requestId = cursoProfesoresRequestRef.current + 1;
    cursoProfesoresRequestRef.current = requestId;
    if (!cursoId) {
      setCursoProfesores([]);
      setCursoProfesoresError('No pudimos identificar el curso seleccionado.');
      setCursoProfesoresLoading(false);
      return;
    }

    setCursoProfesoresLoading(true);
    setCursoProfesoresError('');
    try {
      const profesores = await cursoService.getProfesoresPorCurso(cursoId);
      if (cursoProfesoresRequestRef.current !== requestId) {
        return;
      }
      setCursoProfesores(Array.isArray(profesores) ? profesores : []);
    } catch (error) {
      if (cursoProfesoresRequestRef.current !== requestId) {
        return;
      }
      setCursoProfesores([]);
      setCursoProfesoresError(error.message || 'No pudimos cargar los profesores del curso.');
    } finally {
      if (cursoProfesoresRequestRef.current === requestId) {
        setCursoProfesoresLoading(false);
      }
    }
  }, []);

  const loadCursoMaterias = useCallback(async (curso) => {
    const cursoId = getCursoValue(curso, 'id');
    const requestId = cursoMateriasRequestRef.current + 1;
    cursoMateriasRequestRef.current = requestId;
    if (!cursoId) {
      setCursoMaterias([]);
      setCursoMateriasError('No pudimos identificar el curso seleccionado.');
      setCursoMateriasLoading(false);
      return;
    }

    setCursoMateriasLoading(true);
    setCursoMateriasError('');
    try {
      const materias = await cursoService.getCursoEstructuraAcademica(cursoId);
      if (cursoMateriasRequestRef.current !== requestId) {
        return;
      }
      setCursoMaterias(Array.isArray(materias) ? materias : []);
    } catch (error) {
      if (cursoMateriasRequestRef.current !== requestId) {
        return;
      }
      setCursoMaterias([]);
      setCursoMateriasError(error.message || 'No pudimos cargar las materias del curso.');
    } finally {
      if (cursoMateriasRequestRef.current === requestId) {
        setCursoMateriasLoading(false);
      }
    }
  }, []);

  const openAlumnosModal = async (curso) => {
    setCursosNotice('');
    setSelectedCursoForAlumnos(curso);
    setCursoAlumnos([]);
    setCursoAlumnosError('');
    setCursoAlumnosNotice('');
    setCursoAlumnosSearch('');
    setRemovingAlumnoIds([]);
    setAlumnosModalOpen(true);
    await loadCursoAlumnos(curso);
  };

  const openProfesoresModal = async (curso) => {
    setCursosNotice('');
    setSelectedCursoForProfesores(curso);
    setCursoProfesores([]);
    setCursoProfesoresError('');
    setCursoProfesoresSearch('');
    setProfesoresModalOpen(true);
    await loadCursoProfesores(curso);
  };

  const openMateriasModal = async (curso) => {
    setCursosNotice('');
    setSelectedCursoForMaterias(curso);
    setCursoMaterias([]);
    setCursoMateriasError('');
    setCursoMateriasNotice('');
    setRemovingMateriaIds([]);
    setMateriasModalOpen(true);
    await loadCursoMaterias(curso);
  };

  const closeAlumnosModal = () => {
    cursoAlumnosRequestRef.current += 1;
    setAlumnosModalOpen(false);
    setSelectedCursoForAlumnos(null);
    setCursoAlumnos([]);
    setCursoAlumnosLoading(false);
    setCursoAlumnosError('');
    setCursoAlumnosNotice('');
    setCursoAlumnosSearch('');
    setRemovingAlumnoIds([]);
    setRemoveAllAlumnosModal({
      open: false,
      confirmText: '',
      loading: false,
      error: ''
    });
  };

  const closeProfesoresModal = () => {
    cursoProfesoresRequestRef.current += 1;
    setProfesoresModalOpen(false);
    setSelectedCursoForProfesores(null);
    setCursoProfesores([]);
    setCursoProfesoresLoading(false);
    setCursoProfesoresError('');
    setCursoProfesoresSearch('');
  };

  const closeMateriasModal = () => {
    cursoMateriasRequestRef.current += 1;
    setMateriasModalOpen(false);
    setSelectedCursoForMaterias(null);
    setCursoMaterias([]);
    setCursoMateriasLoading(false);
    setCursoMateriasError('');
    setCursoMateriasNotice('');
    setRemovingMateriaIds([]);
    setRemoveAllMateriasModal({
      open: false,
      confirmText: '',
      loading: false,
      error: ''
    });
  };

  const openDeleteCursoModal = (curso) => {
    setCursosNotice('');
    setDeleteCursoModal({
      open: true,
      curso,
      confirmText: '',
      loading: false,
      error: ''
    });
  };

  const closeDeleteCursoModal = () => {
    if (deleteCursoModal.loading) return;
    setDeleteCursoModal({
      open: false,
      curso: null,
      confirmText: '',
      loading: false,
      error: ''
    });
  };

  const filteredCursoAlumnos = useMemo(() => {
    const term = cursoAlumnosSearch.trim().toLowerCase();
    if (!term) {
      return cursoAlumnos;
    }

    return cursoAlumnos.filter((alumno) => {
      const nombre = (alumno.nombre || alumno.Nombre || '').trim();
      const apellido = (alumno.apellido || alumno.Apellido || '').trim();
      const email = (alumno.email || alumno.Email || '').trim();
      const displayName = (alumno.displayName || alumno.DisplayName || `${nombre} ${apellido}`.trim() || email).trim();

      return displayName.toLowerCase().includes(term) || email.toLowerCase().includes(term);
    });
  }, [cursoAlumnos, cursoAlumnosSearch]);

  const filteredCursoProfesores = useMemo(() => {
    const term = cursoProfesoresSearch.trim().toLowerCase();
    if (!term) {
      return cursoProfesores;
    }

    return cursoProfesores.filter((profesor) => {
      const nombre = (profesor.nombre || profesor.Nombre || '').trim();
      const apellido = (profesor.apellido || profesor.Apellido || '').trim();
      const email = (profesor.email || profesor.Email || '').trim();
      const displayName = (profesor.displayName || profesor.DisplayName || `${nombre} ${apellido}`.trim() || email).trim();
      const legajo = (profesor.legajo || profesor.Legajo || '').trim();

      return (
        displayName.toLowerCase().includes(term)
        || email.toLowerCase().includes(term)
        || legajo.toLowerCase().includes(term)
      );
    });
  }, [cursoProfesores, cursoProfesoresSearch]);

  const removeAllAlumnosExpectedText = useMemo(() => {
    if (!selectedCursoForAlumnos) return '';
    return `DESVINCULAR ${formatCourseFullLabel(selectedCursoForAlumnos)}`;
  }, [selectedCursoForAlumnos]);

  const canConfirmRemoveAllAlumnos = useMemo(() => (
    removeAllAlumnosModal.open
    && cursoAlumnos.length > 0
    && normalizeConfirmText(removeAllAlumnosModal.confirmText) === normalizeConfirmText(removeAllAlumnosExpectedText)
  ), [cursoAlumnos.length, removeAllAlumnosExpectedText, removeAllAlumnosModal.confirmText, removeAllAlumnosModal.open]);

  const openRemoveAllAlumnosModal = () => {
    if (!selectedCursoForAlumnos || cursoAlumnos.length === 0 || cursoAlumnosLoading || removingAlumnoIds.length > 0) {
      return;
    }

    setCursoAlumnosError('');
    setCursoAlumnosNotice('');
    setRemoveAllAlumnosModal({
      open: true,
      confirmText: '',
      loading: false,
      error: ''
    });
  };

  const closeRemoveAllAlumnosModal = () => {
    if (removeAllAlumnosModal.loading) return;
    setRemoveAllAlumnosModal({
      open: false,
      confirmText: '',
      loading: false,
      error: ''
    });
  };

  const handleRemoveAlumnoCurso = async (alumnoId) => {
    const cursoId = getCursoValue(selectedCursoForAlumnos, 'id');
    if (!cursoId || !alumnoId) {
      setCursoAlumnosError('No pudimos identificar el curso o el alumno seleccionado.');
      return;
    }

    setRemovingAlumnoIds((prev) => (prev.includes(alumnoId) ? prev : [...prev, alumnoId]));
    setCursoAlumnosError('');
    setCursoAlumnosNotice('');

    try {
      const response = await cursoService.removeAlumnos(cursoId, [alumnoId]);
      const desasociados = response?.desasociados || response?.Desasociados || [];
      const yaInactivos = response?.yaInactivos || response?.YaInactivos || [];
      const alumnosNoAsociados = response?.alumnosNoAsociados || response?.AlumnosNoAsociados || [];

      if (Array.isArray(desasociados) && desasociados.includes(alumnoId)) {
        setCursoAlumnos((prevAlumnos) => {
          const nextAlumnos = prevAlumnos.filter((item) => (item.id || item.Id) !== alumnoId);
          syncCursoAlumnoCount(cursoId, nextAlumnos.length);
          return nextAlumnos;
        });
        return;
      }

      if (Array.isArray(yaInactivos) && yaInactivos.includes(alumnoId)) {
        setCursoAlumnos((prevAlumnos) => {
          const nextAlumnos = prevAlumnos.filter((item) => (item.id || item.Id) !== alumnoId);
          syncCursoAlumnoCount(cursoId, nextAlumnos.length);
          return nextAlumnos;
        });
        return;
      }

      if (Array.isArray(alumnosNoAsociados) && alumnosNoAsociados.includes(alumnoId)) {
        setCursoAlumnosError('El alumno ya no estaba vinculado al curso.');
        return;
      }

      setCursoAlumnosError('No pudimos desvincular el alumno del curso.');
    } catch (error) {
      setCursoAlumnosError(error.message || 'No pudimos desvincular el alumno del curso.');
    } finally {
      setRemovingAlumnoIds((prev) => prev.filter((id) => id !== alumnoId));
    }
  };

  const handleRemoveAllAlumnosCurso = async () => {
    const cursoId = getCursoValue(selectedCursoForAlumnos, 'id');
    const alumnoIds = cursoAlumnos
      .map((alumno) => alumno.id || alumno.Id)
      .filter(Boolean);

    if (!cursoId || alumnoIds.length === 0 || !canConfirmRemoveAllAlumnos) {
      return;
    }

    setRemoveAllAlumnosModal((prev) => ({
      ...prev,
      loading: true,
      error: ''
    }));
    setRemovingAlumnoIds(alumnoIds);
    setCursoAlumnosError('');
    setCursoAlumnosNotice('');

    try {
      const response = await cursoService.removeAlumnos(cursoId, alumnoIds);
      const desasociados = response?.desasociados || response?.Desasociados || [];
      const yaInactivos = response?.yaInactivos || response?.YaInactivos || [];
      const alumnosNoAsociados = response?.alumnosNoAsociados || response?.AlumnosNoAsociados || [];
      const alumnosNoEncontrados = response?.alumnosNoEncontrados || response?.AlumnosNoEncontrados || [];
      const alumnosNoColegio = response?.alumnosNoColegio || response?.AlumnosNoColegio || [];
      const alumnosNoRol = response?.alumnosNoRol || response?.AlumnosNoRol || [];

      const processedIds = new Set([
        ...desasociados,
        ...yaInactivos,
        ...alumnosNoAsociados
      ]);

      const nextAlumnos = cursoAlumnos.filter((item) => !processedIds.has(item.id || item.Id));
      const removedCount = cursoAlumnos.length - nextAlumnos.length;
      const failedCount = alumnosNoEncontrados.length + alumnosNoColegio.length + alumnosNoRol.length;

      setCursoAlumnos(nextAlumnos);
      syncCursoAlumnoCount(cursoId, nextAlumnos.length);

      if (removedCount > 0) {
        const baseMessage = removedCount === 1
          ? 'Se desvinculó 1 alumno del curso.'
          : `Se desvincularon ${removedCount} alumnos del curso.`;
        setCursoAlumnosNotice(failedCount > 0 ? `${baseMessage} Quedaron ${failedCount} casos sin cambios.` : baseMessage);
        setRemoveAllAlumnosModal({
          open: false,
          confirmText: '',
          loading: false,
          error: ''
        });
      } else if (failedCount > 0) {
        setRemoveAllAlumnosModal((prev) => ({
          ...prev,
          loading: false,
          error: 'No pudimos desvincular a todos los alumnos seleccionados del curso.'
        }));
        return;
      } else {
        setRemoveAllAlumnosModal({
          open: false,
          confirmText: '',
          loading: false,
          error: ''
        });
      }
    } catch (error) {
      setRemoveAllAlumnosModal((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'No pudimos desvincular a todos los alumnos del curso.'
      }));
    } finally {
      setRemovingAlumnoIds([]);
    }
  };

  const handleRemoveMateriaCurso = async (materiaId) => {
    const cursoId = getCursoValue(selectedCursoForMaterias, 'id');
    if (!cursoId || !materiaId) {
      setCursoMateriasError('No pudimos identificar el curso o la materia seleccionada.');
      return;
    }

    setRemovingMateriaIds((prev) => (prev.includes(materiaId) ? prev : [...prev, materiaId]));
    setCursoMateriasError('');
    setCursoMateriasNotice('');

    try {
      const response = await cursoService.removeMaterias(cursoId, [materiaId]);
      const desasociadas = response?.desasociadas || response?.Desasociadas || [];
      const yaInactivas = response?.yaInactivas || response?.YaInactivas || [];
      const materiasNoAsociadas = response?.materiasNoAsociadas || response?.MateriasNoAsociadas || [];

      if (Array.isArray(desasociadas) && desasociadas.includes(materiaId)) {
        setCursoMaterias((prevMaterias) => {
          const nextMaterias = prevMaterias.filter((item) => (item.idMateria || item.IdMateria) !== materiaId);
          syncCursoMateriaCount(cursoId, nextMaterias.length);
          return nextMaterias;
        });
        return;
      }

      if (Array.isArray(yaInactivas) && yaInactivas.includes(materiaId)) {
        setCursoMaterias((prevMaterias) => {
          const nextMaterias = prevMaterias.filter((item) => (item.idMateria || item.IdMateria) !== materiaId);
          syncCursoMateriaCount(cursoId, nextMaterias.length);
          return nextMaterias;
        });
        return;
      }

      if (Array.isArray(materiasNoAsociadas) && materiasNoAsociadas.includes(materiaId)) {
        setCursoMateriasError('La materia ya no estaba vinculada al curso.');
        return;
      }

      setCursoMateriasError('No pudimos desvincular la materia del curso.');
    } catch (error) {
      setCursoMateriasError(error.message || 'No pudimos desvincular la materia del curso.');
    } finally {
      setRemovingMateriaIds((prev) => prev.filter((id) => id !== materiaId));
    }
  };

  const removeAllMateriasExpectedText = useMemo(() => {
    if (!selectedCursoForMaterias) return '';
    return `DESVINCULAR MATERIAS ${formatCourseFullLabel(selectedCursoForMaterias)}`;
  }, [selectedCursoForMaterias]);

  const canConfirmRemoveAllMaterias = useMemo(() => (
    removeAllMateriasModal.open
    && cursoMaterias.length > 0
    && normalizeConfirmText(removeAllMateriasModal.confirmText) === normalizeConfirmText(removeAllMateriasExpectedText)
  ), [cursoMaterias.length, removeAllMateriasExpectedText, removeAllMateriasModal.confirmText, removeAllMateriasModal.open]);

  const openRemoveAllMateriasModal = () => {
    if (!selectedCursoForMaterias || cursoMaterias.length === 0 || cursoMateriasLoading || removingMateriaIds.length > 0) {
      return;
    }

    setCursoMateriasError('');
    setCursoMateriasNotice('');
    setRemoveAllMateriasModal({
      open: true,
      confirmText: '',
      loading: false,
      error: ''
    });
  };

  const closeRemoveAllMateriasModal = () => {
    if (removeAllMateriasModal.loading) return;
    setRemoveAllMateriasModal({
      open: false,
      confirmText: '',
      loading: false,
      error: ''
    });
  };

  const handleRemoveAllMateriasCurso = async () => {
    const cursoId = getCursoValue(selectedCursoForMaterias, 'id');
    const materiaIds = cursoMaterias
      .map((materia) => materia.idMateria || materia.IdMateria)
      .filter(Boolean);

    if (!cursoId || materiaIds.length === 0 || !canConfirmRemoveAllMaterias) {
      return;
    }

    setRemoveAllMateriasModal((prev) => ({
      ...prev,
      loading: true,
      error: ''
    }));
    setRemovingMateriaIds(materiaIds);
    setCursoMateriasError('');
    setCursoMateriasNotice('');

    try {
      const response = await cursoService.removeMaterias(cursoId, materiaIds);
      const desasociadas = response?.desasociadas || response?.Desasociadas || [];
      const yaInactivas = response?.yaInactivas || response?.YaInactivas || [];
      const materiasNoAsociadas = response?.materiasNoAsociadas || response?.MateriasNoAsociadas || [];
      const materiasNoEncontradas = response?.materiasNoEncontradas || response?.MateriasNoEncontradas || [];

      const processedIds = new Set([
        ...desasociadas,
        ...yaInactivas,
        ...materiasNoAsociadas
      ]);

      const nextMaterias = cursoMaterias.filter((item) => !processedIds.has(item.idMateria || item.IdMateria));
      const removedCount = cursoMaterias.length - nextMaterias.length;
      const failedCount = materiasNoEncontradas.length;

      setCursoMaterias(nextMaterias);
      syncCursoMateriaCount(cursoId, nextMaterias.length);

      if (removedCount > 0) {
        const baseMessage = removedCount === 1
          ? 'Se desvinculó 1 materia del curso.'
          : `Se desvincularon ${removedCount} materias del curso.`;
        setCursoMateriasNotice(
          failedCount > 0
            ? `${baseMessage} Quedaron ${failedCount} casos sin cambios.`
            : `${baseMessage} También se desactivaron sus relaciones curso-materia-profesor.`
        );
        setRemoveAllMateriasModal({
          open: false,
          confirmText: '',
          loading: false,
          error: ''
        });
      } else if (failedCount > 0) {
        setRemoveAllMateriasModal((prev) => ({
          ...prev,
          loading: false,
          error: 'No pudimos desvincular todas las materias seleccionadas del curso.'
        }));
        return;
      } else {
        setRemoveAllMateriasModal({
          open: false,
          confirmText: '',
          loading: false,
          error: ''
        });
      }
    } catch (error) {
      setRemoveAllMateriasModal((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'No pudimos desvincular todas las materias del curso.'
      }));
    } finally {
      setRemovingMateriaIds([]);
    }
  };

  const handleImportCursos = async () => {
    if (!activeColegioId) {
      setImportMessage('Selecciona un colegio antes de importar cursos.');
      return;
    }

    if (!importFile) {
      setImportMessage('Debes seleccionar un archivo .xlsx.');
      return;
    }

    setImportLoading(true);
    setImportMessage('');

    try {
      const response = await procesosAsincronicosService.importarCursosExcel({
        idColegio: activeColegioId,
        archivo: importFile
      });

      const registros = response?.cantidadDeRegistros ?? response?.CantidadDeRegistros ?? 0;
      const erroresIniciales = response?.cantidadConErrorInicial ?? response?.CantidadConErrorInicial ?? 0;
      const mensaje = response?.mensaje || response?.Mensaje || 'La solicitud de importación fue creada correctamente.';
      const processId = response?.procesoId || response?.ProcesoId || null;

      navigate('/admin-dashboard', {
        state: {
          tab: 'procesos-asincronicos',
          selectedProcesoId: processId,
          procesosNotice: `${mensaje} Registros detectados: ${registros}. Errores iniciales: ${erroresIniciales}.`
        }
      });
    } catch (error) {
      setImportMessage(error.message || 'No pudimos crear la solicitud de importación.');
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportAlumnosCurso = async () => {
    const cursoId = getCursoValue(selectedCursoForImport, 'id');

    if (!cursoId) {
      setImportAlumnosMessage('No pudimos identificar el curso seleccionado.');
      return;
    }

    if (!activeColegioId) {
      setImportAlumnosMessage('Selecciona un colegio antes de importar alumnos.');
      return;
    }

    if (!importAlumnosFile) {
      setImportAlumnosMessage('Debes seleccionar un archivo .xlsx.');
      return;
    }

    setImportAlumnosLoading(true);
    setImportAlumnosMessage('');

    try {
      const response = await procesosAsincronicosService.importarAlumnosCursoExcel({
        cursoId,
        idColegio: activeColegioId,
        archivo: importAlumnosFile
      });

      const registros = response?.cantidadDeRegistros ?? response?.CantidadDeRegistros ?? 0;
      const erroresIniciales = response?.cantidadConErrorInicial ?? response?.CantidadConErrorInicial ?? 0;
      const mensaje = response?.mensaje || response?.Mensaje || 'La solicitud de importación fue creada correctamente.';
      const processId = response?.procesoId || response?.ProcesoId || null;

      navigate('/admin-dashboard', {
        state: {
          tab: 'procesos-asincronicos',
          selectedProcesoId: processId,
          procesosNotice: `${mensaje} Registros detectados: ${registros}. Errores iniciales: ${erroresIniciales}.`
        }
      });
    } catch (error) {
      setImportAlumnosMessage(error.message || 'No pudimos crear la solicitud de importación.');
    } finally {
      setImportAlumnosLoading(false);
    }
  };

  const deleteCursoExpectedText = useMemo(() => {
    if (!deleteCursoModal.curso) return '';
    return `ELIMINAR ${formatCourseFullLabel(deleteCursoModal.curso)}`;
  }, [deleteCursoModal.curso]);

  const canConfirmDeleteCurso = useMemo(() => (
    deleteCursoModal.curso
    && normalizeConfirmText(deleteCursoModal.confirmText) === normalizeConfirmText(deleteCursoExpectedText)
  ), [deleteCursoExpectedText, deleteCursoModal.confirmText, deleteCursoModal.curso]);

  const handleConfirmDeleteCurso = async () => {
    const curso = deleteCursoModal.curso;
    const cursoId = getCursoValue(curso, 'id');
    if (!cursoId || !canConfirmDeleteCurso) {
      return;
    }

    setDeleteCursoModal((prev) => ({ ...prev, loading: true, error: '' }));

    try {
      await cursoService.deleteCurso(cursoId);
      const courseLabel = formatCourseFullLabel(curso);
      setDeleteCursoModal({
        open: false,
        curso: null,
        confirmText: '',
        loading: false,
        error: ''
      });
      setCursosNotice(`El curso ${courseLabel} fue eliminado junto con sus asociaciones.`);
      await loadCursos();
    } catch (error) {
      setDeleteCursoModal((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'No pudimos eliminar el curso.'
      }));
    }
  };

  return (
    <div className="dashboard-grid cursos-dashboard-grid">
      <div className="hero-card cursos-hero-card">
        <div className="hero-content">
          <div>
            <span className="hero-badge">Cursos</span>
            <h2>Gestiona cursos por ciclo, año y división</h2>
            <p className="subtitle">
              Organiza la estructura académica del colegio y carga nuevas secciones desde Excel con asociación opcional de profesores.
            </p>
            <div className="cursos-hero-meta">
              <span className="curso-meta-pill">
                <i className="fas fa-calendar-alt" />
                Template: CicloLectivo, Año, Division y Profesores
              </span>
              <span className="curso-meta-pill">
                <i className="fas fa-tasks" />
                Procesamiento asíncrono con seguimiento por fila
              </span>
            </div>
          </div>
          <div className="hero-actions cursos-hero-actions">
            <a className="secondary-btn" href={templateUrl} download>
              <i className="fas fa-download" />
              <span>Descargar template</span>
            </a>
            <button className="secondary-btn" type="button" onClick={openImportModal}>
              <i className="fas fa-file-excel" />
              <span>Importar Excel</span>
            </button>
            <button
              className="primary-btn"
              type="button"
              onClick={() => navigate('/admin/cursos/nuevo', { state: { idColegio: activeColegioId } })}
            >
              <i className="fas fa-plus" />
              <span>Nuevo curso</span>
            </button>
          </div>
        </div>

        <div className="stats-grid">
          <article className="stat-card">
            <div className="stat-icon primary">
              <i className="fas fa-layer-group" />
            </div>
            <div>
              <p className="count-label">Total cursos</p>
              <p className="count-number">{cursosTotal}</p>
            </div>
          </article>
          <article className="stat-card">
            <div className="stat-icon success">
              <i className="fas fa-calendar-day" />
            </div>
            <div>
              <p className="count-label">Ciclos visibles</p>
              <p className="count-number">{cursosVisibleStats.ciclos}</p>
            </div>
          </article>
          <article className="stat-card">
            <div className="stat-icon warning">
              <i className="fas fa-school" />
            </div>
            <div>
              <p className="count-label">Divisiones visibles</p>
              <p className="count-number">{cursosVisibleStats.divisiones}</p>
            </div>
          </article>
          <article className="stat-card">
            <div className="stat-icon info">
              <i className="fas fa-chalkboard-teacher" />
            </div>
            <div>
              <p className="count-label">Profesores listados</p>
              <p className="count-number">{cursosVisibleStats.profesores}</p>
            </div>
          </article>
        </div>
      </div>

      <div className="content-card full-width">
        <div className="card-header cursos-card-header">
          <div className="card-title">
            <i className="fas fa-layer-group" />
            <span>Listado de cursos</span>
          </div>
          <div className="cursos-toolbar">
            <div className="professor-search">
              <i className="fas fa-search" />
              <input
                type="text"
                placeholder="Buscar por ciclo, año o división"
                value={cursosSearch}
                onChange={(e) => setCursosSearch(e.target.value)}
              />
            </div>
            <div className="cursos-filter-select">
              <i className="fas fa-calendar-alt" aria-hidden="true" />
              <select
                aria-label="Filtrar por ciclo lectivo"
                value={cicloFilter}
                onChange={(e) => setCicloFilter(e.target.value)}
              >
                <option value="">Todos los ciclos</option>
                {cicloOptions.map((ciclo) => (
                  <option key={ciclo} value={ciclo}>
                    {ciclo}
                  </option>
                ))}
              </select>
            </div>
            <div className="cursos-filter-select">
              <i className="fas fa-sort-numeric-up" aria-hidden="true" />
              <select
                aria-label="Filtrar por año"
                value={anioFilter}
                onChange={(e) => setAnioFilter(e.target.value)}
              >
                <option value="">Todos los años</option>
                {anioOptions.map((anio) => (
                  <option key={anio} value={anio}>
                    {anio}°
                  </option>
                ))}
              </select>
            </div>
            <div className="cursos-filter-select">
              <i className="fas fa-code-branch" aria-hidden="true" />
              <select
                aria-label="Filtrar por división"
                value={divisionFilter}
                onChange={(e) => setDivisionFilter(e.target.value)}
              >
                <option value="">Todas las divisiones</option>
                {divisionOptions.map((division) => (
                  <option key={division} value={division}>
                    {division}
                  </option>
                ))}
              </select>
            </div>
            <button className="refresh-btn" type="button" onClick={loadCursos} disabled={cursosLoading}>
              <i className={`fas ${cursosLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
          </div>
        </div>

        <div className="card-content professors-table">
          {(cicloOptions.length > 0 || anioOptions.length > 0 || divisionOptions.length > 0) && (
            <div className="cursos-chip-strip">
              {cicloOptions.length > 0 && (
                <span className="curso-filter-chip">
                  <i className="fas fa-calendar-alt" />
                  {cicloOptions.length} ciclo(s) en esta página
                </span>
              )}
              {anioOptions.length > 0 && (
                <span className="curso-filter-chip">
                  <i className="fas fa-sort-numeric-up" />
                  Años BD: {anioOptions.join(', ')}
                </span>
              )}
              {divisionOptions.length > 0 && (
                <span className="curso-filter-chip">
                  <i className="fas fa-code-branch" />
                  Divisiones BD: {divisionOptions.join(', ')}
                </span>
              )}
            </div>
          )}

          {cursosNotice && (
            <div className="curso-delete-feedback" role="status">
              <i className="fas fa-check-circle" />
              <span>{cursosNotice}</span>
            </div>
          )}

          {cursosLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin" />
              </div>
              <p>Cargando cursos...</p>
            </div>
          )}

          {!cursosLoading && cursosError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle" />
              </div>
              <p>{cursosError}</p>
              <button className="refresh-btn" type="button" onClick={loadCursos}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!cursosLoading && !cursosError && filteredCursos.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-layer-group" />
              </div>
              <p>No hay cursos que coincidan con los filtros actuales.</p>
            </div>
          )}

          {!cursosLoading && !cursosError && filteredCursos.length > 0 && (
            <div className="data-table-wrapper">
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                <TableExportActions
                  tableSelector="#admin-cursos-main-table"
                  fileName="cursos"
                  title="Listado de cursos"
                  compact
                  disabled={filteredCursos.length === 0}
                />
              </div>
              <table id="admin-cursos-main-table" className="data-table cursos-table">
                <thead>
                  <tr>
                    <th className="curso-main-column">Curso</th>
                    <th className="text-center width-sm">Materias</th>
                    <th className="text-center width-sm">Ciclo</th>
                    <th className="text-center width-sm">Año</th>
                    <th className="text-center width-sm">División</th>
                    <th className="text-center width-sm">Profesores</th>
                    <th className="text-center width-sm">Alumnos</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCursos.map((curso) => {
                    const cursoId = getCursoValue(curso, 'id');
                    const activo = typeof getCursoValue(curso, 'activo') === 'boolean' ? getCursoValue(curso, 'activo') : true;
                    const cicloLectivo = getCursoValue(curso, 'cicloLectivo');
                    const valorAnio = getCursoValue(curso, 'valorAnio');
                    const division = getCursoValue(curso, 'division');
                    const cantidadMaterias = Number(getCursoValue(curso, 'cantidadMaterias') || 0);
                    const cantidadProfesores = Number(getCursoValue(curso, 'cantidadProfesores') || 0);
                    const cantidadAlumnos = Number(getCursoValue(curso, 'cantidadAlumnos') || 0);
                    const cantidadAulas = Number(getCursoValue(curso, 'cantidadAulas') || 0);
                    const courseLabel = formatCourseLabel(curso);
                    const colegioNombre = getCursoValue(curso, 'nombreColegio') || 'Colegio';

                    return (
                      <tr key={cursoId}>
                        <td className="curso-main-cell" title={`${courseLabel} • ${colegioNombre}`}>
                          <div className="prof-name">
                            <span className="name" title={courseLabel}>{courseLabel}</span>
                            <span className="muted" title={colegioNombre}>{colegioNombre}</span>
                          </div>
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="curso-count-trigger"
                            onClick={() => openMateriasModal(curso)}
                            title="Ver materias del curso"
                          >
                            {cantidadMaterias}
                          </button>
                        </td>
                        <td className="text-center">{cicloLectivo || '—'}</td>
                        <td className="text-center">{valorAnio ? `${valorAnio}°` : '—'}</td>
                        <td className="text-center">
                          <span className="curso-division-badge">{division || '—'}</span>
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="curso-count-trigger"
                            onClick={() => openProfesoresModal(curso)}
                            title="Ver profesores del curso"
                          >
                            {cantidadProfesores}
                          </button>
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="curso-count-trigger"
                            onClick={() => openAlumnosModal(curso)}
                            title="Ver alumnos del curso"
                          >
                            {cantidadAlumnos}
                          </button>
                        </td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="action-buttons">
                            <button
                              type="button"
                              className="action-edit-btn"
                              onClick={() => navigate(`/admin/cursos/${cursoId}/editar`, { state: { curso, idColegio: activeColegioId } })}
                              title="Editar curso"
                            >
                              <i className="fas fa-edit" />
                            </button>
                            <button
                              type="button"
                              className="action-message-btn"
                              onClick={() => openImportAlumnosModal(curso)}
                              title="Importar alumnos"
                            >
                              <i className="fas fa-user-plus" />
                            </button>
                            <button
                              type="button"
                              className="action-edit-btn"
                              onClick={() => setEvaluacionesCurso({ curso, initialView: 'lista' })}
                              title="Evaluaciones y notas"
                            >
                              <i className="fas fa-clipboard-check" />
                            </button>
                            <button
                              type="button"
                              className="action-remove-btn"
                              onClick={() => openDeleteCursoModal({
                                ...curso,
                                cantidadAulas,
                                CantidadAulas: cantidadAulas
                              })}
                              title="Eliminar curso"
                            >
                              <i className="fas fa-trash-alt" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!cursosLoading && !cursosError && cursosTotal > 0 && (
            renderPagination(cursosTotal, cursosPage, setCursosPage, cursosPageSize, setCursosPageSize)
          )}
        </div>
      </div>

      {importModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h3><i className="fas fa-file-import" /> Importar cursos</h3>
              <button className="close-btn" onClick={closeImportModal} disabled={importLoading}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); handleImportCursos(); }} className="prof-create-form">
                <div className="form-field">
                  <label>Template</label>
                  <a className="secondary-btn" href={templateUrl} download style={{ width: 'fit-content' }}>
                    <i className="fas fa-download" />
                    <span>Descargar template</span>
                  </a>
                </div>
                <div className="form-field">
                  <label>Archivo Excel (.xlsx)</label>
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={(e) => {
                      const [file] = Array.from(e.target.files || []);
                      setImportFile(file || null);
                    }}
                  />
                  <small className="helper-text">
                    Columnas requeridas: <strong>CicloLectivo</strong>, <strong>Año</strong>, <strong>Division</strong> y <strong>Profesores</strong>.
                    Si la columna Profesores viene vacía, el curso se crea igual y podrás asociarlos luego.
                  </small>
                </div>
                {importMessage && (
                  <div className="modal-message" style={{ marginTop: '0.5rem' }}>
                    {importLoading && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {importMessage}
                  </div>
                )}
              </form>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="btn btn-secondary" onClick={closeImportModal} disabled={importLoading}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleImportCursos} disabled={importLoading}>
                {importLoading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-cloud-upload-alt" />}
                <span>Crear solicitud</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteCursoModal.open && (
        <div className="modal-overlay" onClick={closeDeleteCursoModal}>
          <div className="modal-card curso-delete-modal" onClick={(event) => event.stopPropagation()}>
            <div className="curso-delete-modal-header">
              <div className="curso-delete-modal-heading">
                <span className="curso-delete-modal-eyebrow">Eliminación definitiva</span>
                <h3>
                  <i className="fas fa-exclamation-triangle" />
                  Eliminar curso y asociaciones
                </h3>
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={closeDeleteCursoModal}
                disabled={deleteCursoModal.loading}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="curso-delete-modal-body">
              <p className="curso-delete-modal-intro">
                Vas a eliminar de forma permanente el curso <strong>{formatCourseFullLabel(deleteCursoModal.curso)}</strong>.
                Esta acción también limpiará todas las vinculaciones académicas y operativas que dependan de ese curso.
              </p>

              <div className="curso-delete-summary-grid">
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Ciclo lectivo</span>
                  <strong>{getCursoValue(deleteCursoModal.curso, 'cicloLectivo') || '—'}</strong>
                </div>
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Año y división</span>
                  <strong>{formatCourseLabel(deleteCursoModal.curso)}</strong>
                </div>
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Aulas vinculadas</span>
                  <strong>{Number(getCursoValue(deleteCursoModal.curso, 'cantidadAulas') || 0)}</strong>
                </div>
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Materias asociadas</span>
                  <strong>{Number(getCursoValue(deleteCursoModal.curso, 'cantidadMaterias') || 0)}</strong>
                </div>
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Profesores asociados</span>
                  <strong>{Number(getCursoValue(deleteCursoModal.curso, 'cantidadProfesores') || 0)}</strong>
                </div>
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Alumnos vinculados</span>
                  <strong>{Number(getCursoValue(deleteCursoModal.curso, 'cantidadAlumnos') || 0)}</strong>
                </div>
              </div>

              <div className="curso-delete-impact">
                <p className="curso-delete-impact-title">Qué se eliminará</p>
                <ul>
                  <li>El registro del curso seleccionado.</li>
                  <li>Todas las aulas vinculadas a ese curso y sus recursos dependientes.</li>
                  <li>Las asociaciones del curso con materias, profesores y alumnos.</li>
                  <li>Las relaciones académicas derivadas entre curso, materia y profesor.</li>
                </ul>
              </div>

              <div className="curso-delete-confirm-box">
                <label htmlFor="curso-delete-confirm-input">
                  Para confirmar, escribe exactamente:
                </label>
                <code>{deleteCursoExpectedText}</code>
                <input
                  id="curso-delete-confirm-input"
                  type="text"
                  value={deleteCursoModal.confirmText}
                  onChange={(event) => setDeleteCursoModal((prev) => ({
                    ...prev,
                    confirmText: event.target.value,
                    error: ''
                  }))}
                  placeholder={deleteCursoExpectedText}
                  autoComplete="off"
                  disabled={deleteCursoModal.loading}
                />
                {deleteCursoModal.confirmText.trim() && (
                  <span className={`curso-delete-confirm-hint ${canConfirmDeleteCurso ? 'valid' : 'invalid'}`}>
                    {canConfirmDeleteCurso
                      ? 'Confirmación válida. Ya puedes eliminar el curso.'
                      : 'La confirmación no coincide con el texto solicitado.'}
                  </span>
                )}
              </div>

              {deleteCursoModal.error && (
                <div className="curso-delete-error">
                  <i className="fas fa-exclamation-circle" />
                  <span>{deleteCursoModal.error}</span>
                </div>
              )}
            </div>

            <div className="confirm-modal-actions curso-delete-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDeleteCursoModal}
                disabled={deleteCursoModal.loading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger curso-delete-confirm-btn"
                onClick={handleConfirmDeleteCurso}
                disabled={!canConfirmDeleteCurso || deleteCursoModal.loading}
              >
                {deleteCursoModal.loading ? 'Eliminando...' : 'Eliminar curso'}
              </button>
            </div>
          </div>
        </div>
      )}

      {importAlumnosModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h3><i className="fas fa-file-import" /> Importar alumnos al curso</h3>
              <button className="close-btn" onClick={closeImportAlumnosModal} disabled={importAlumnosLoading}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginTop: 0, color: '#475569' }}>
                Curso seleccionado: <strong>{selectedCursoForImport ? formatCourseLabel(selectedCursoForImport) : '—'}</strong>
              </p>
              <form onSubmit={(e) => { e.preventDefault(); handleImportAlumnosCurso(); }} className="prof-create-form">
                <div className="form-field">
                  <label>Template</label>
                  <a className="secondary-btn" href={alumnosTemplateUrl} download style={{ width: 'fit-content' }}>
                    <i className="fas fa-download" />
                    <span>Descargar template</span>
                  </a>
                </div>
                <div className="form-field">
                  <label>Archivo Excel (.xlsx)</label>
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={(e) => {
                      const [file] = Array.from(e.target.files || []);
                      setImportAlumnosFile(file || null);
                    }}
                  />
                  <small className="helper-text">
                    Columnas requeridas: <strong>Nombre</strong>, <strong>Apellido</strong> y <strong>Email</strong>.
                    Si el alumno no existe, se creará con password inicial <strong>123456</strong> y luego se asociará al curso.
                  </small>
                </div>
                {importAlumnosMessage && (
                  <div className="modal-message" style={{ marginTop: '0.5rem' }}>
                    {importAlumnosLoading && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {importAlumnosMessage}
                  </div>
                )}
              </form>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="btn btn-secondary" onClick={closeImportAlumnosModal} disabled={importAlumnosLoading}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleImportAlumnosCurso} disabled={importAlumnosLoading}>
                {importAlumnosLoading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-cloud-upload-alt" />}
                <span>Crear solicitud</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {materiasModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '980px', width: 'min(980px, 96vw)' }}>
            <div className="modal-header">
              <h3><i className="fas fa-book" /> Materias del curso</h3>
              <button className="close-btn" onClick={closeMateriasModal}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <p style={{ margin: 0, color: '#475569' }}>
                  Curso seleccionado: <strong>{selectedCursoForMaterias ? formatCourseLabel(selectedCursoForMaterias) : '—'}</strong>
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={openRemoveAllMateriasModal}
                    disabled={cursoMateriasLoading || removingMateriaIds.length > 0 || cursoMaterias.length === 0}
                  >
                    <i className="fas fa-unlink" />
                    <span>Desvincular todas las materias</span>
                  </button>
                  <button
                    className="refresh-btn"
                    type="button"
                    onClick={() => loadCursoMaterias(selectedCursoForMaterias)}
                    disabled={cursoMateriasLoading || removeAllMateriasModal.loading}
                  >
                    <i className={`fas ${cursoMateriasLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
                  </button>
                </div>
              </div>

              {cursoMateriasNotice && (
                <div
                  className="modal-message"
                  style={{
                    marginBottom: '1rem',
                    borderColor: '#bbf7d0',
                    background: '#dcfce7',
                    color: '#166534'
                  }}
                >
                  <i className="fas fa-check-circle" style={{ marginRight: '0.35rem' }} />
                  {cursoMateriasNotice}
                </div>
              )}

              {cursoMateriasLoading && (
                <div className="data-table-loading">
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin" />
                  </div>
                  <p>Cargando materias del curso...</p>
                </div>
              )}

              {!cursoMateriasLoading && cursoMateriasError && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-exclamation-triangle" />
                  </div>
                  <p>{cursoMateriasError}</p>
                </div>
              )}

              {!cursoMateriasLoading && !cursoMateriasError && cursoMaterias.length === 0 && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-book-open" />
                  </div>
                  <p>Este curso no tiene materias asociadas.</p>
                </div>
              )}

              {!cursoMateriasLoading && !cursoMateriasError && cursoMaterias.length > 0 && (
                <div className="data-table-wrapper">
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                    <TableExportActions
                      tableSelector="#admin-cursos-materias-table"
                      fileName={`curso-materias-${formatCourseLabel(selectedCursoForMaterias || {})}`}
                      title={`Materias de ${formatCourseLabel(selectedCursoForMaterias || {})}`}
                      compact
                      disabled={cursoMaterias.length === 0}
                    />
                  </div>
                  <table id="admin-cursos-materias-table" className="data-table">
                    <thead>
                      <tr>
                        <th>Materia</th>
                        <th>Área</th>
                        <th>Profesores</th>
                        <th className="text-center width-sm">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cursoMaterias.map((materia) => {
                        const materiaId = materia.idMateria || materia.IdMateria;
                        const nombre = materia.nombreMateria || materia.NombreMateria || 'Materia';
                        const codigo = materia.codigoMateria || materia.CodigoMateria || '';
                        const area = materia.nombreArea || materia.NombreArea || '—';
                        const profesores = Array.isArray(materia.profesores || materia.Profesores) ? (materia.profesores || materia.Profesores) : [];
                        const removing = removingMateriaIds.includes(materiaId);

                        return (
                          <tr key={materiaId}>
                            <td>
                              <div className="prof-name">
                                <span className="name">{codigo ? `${nombre} (${codigo})` : nombre}</span>
                                <span className="muted">{materia.descripcionMateria || materia.DescripcionMateria || 'Sin descripción'}</span>
                              </div>
                            </td>
                            <td>{area}</td>
                            <td>
                              {profesores.length > 0
                                ? profesores.map((profesor) => profesor.displayName || profesor.DisplayName || profesor.email || profesor.Email || 'Profesor').join(', ')
                                : 'Sin profesores'}
                            </td>
                            <td className="text-center">
                              <div className="action-buttons">
                                <button
                                  type="button"
                                  className="action-remove-btn"
                                  onClick={() => handleRemoveMateriaCurso(materiaId)}
                                  disabled={removing}
                                  title="Quitar materia del curso"
                                >
                                  <i className={`fas ${removing ? 'fa-spinner fa-spin' : 'fa-unlink'}`} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={closeMateriasModal}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {alumnosModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '980px', width: 'min(980px, 96vw)' }}>
            <div className="modal-header">
              <h3><i className="fas fa-user-graduate" /> Alumnos del curso</h3>
              <button className="close-btn" onClick={closeAlumnosModal}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <p style={{ margin: 0, color: '#475569' }}>
                  Curso seleccionado: <strong>{selectedCursoForAlumnos ? formatCourseLabel(selectedCursoForAlumnos) : '—'}</strong>
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={openRemoveAllAlumnosModal}
                    disabled={cursoAlumnosLoading || removingAlumnoIds.length > 0 || cursoAlumnos.length === 0}
                  >
                    <i className="fas fa-user-slash" />
                    <span>Desvincular a todos los alumnos</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setEvaluacionesCurso({ curso: selectedCursoForAlumnos, initialView: 'resumen' })}
                  >
                    <i className="fas fa-chart-bar" />
                    <span>Resumen académico</span>
                  </button>
                  <button
                    className="refresh-btn"
                    type="button"
                    onClick={() => {
                      loadCursoAlumnos(selectedCursoForAlumnos);
                      resumenAcademico.reload();
                    }}
                    disabled={cursoAlumnosLoading || removeAllAlumnosModal.loading}
                  >
                    <i className={`fas ${cursoAlumnosLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
                  </button>
                </div>
              </div>

              {cursoAlumnosNotice && (
                <div
                  className="modal-message"
                  style={{
                    marginBottom: '1rem',
                    borderColor: '#bbf7d0',
                    background: '#dcfce7',
                    color: '#166534'
                  }}
                >
                  <i className="fas fa-check-circle" style={{ marginRight: '0.35rem' }} />
                  {cursoAlumnosNotice}
                </div>
              )}

              <div className="professor-search" style={{ width: '100%', marginBottom: '1rem' }}>
                <i className="fas fa-search" />
                <input
                  type="text"
                  placeholder="Buscar alumno por nombre o email"
                  value={cursoAlumnosSearch}
                  onChange={(e) => setCursoAlumnosSearch(e.target.value)}
                />
              </div>

              {cursoAlumnosLoading && (
                <div className="data-table-loading">
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin" />
                  </div>
                  <p>Cargando alumnos del curso...</p>
                </div>
              )}

              {!cursoAlumnosLoading && cursoAlumnosError && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-exclamation-triangle" />
                  </div>
                  <p>{cursoAlumnosError}</p>
                </div>
              )}

              {!cursoAlumnosLoading && !cursoAlumnosError && cursoAlumnos.length === 0 && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-user-slash" />
                  </div>
                  <p>Este curso no tiene alumnos asociados.</p>
                </div>
              )}

              {!cursoAlumnosLoading && !cursoAlumnosError && cursoAlumnos.length > 0 && filteredCursoAlumnos.length === 0 && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-search" />
                  </div>
                  <p>No hay alumnos que coincidan con la búsqueda.</p>
                </div>
              )}

              {!cursoAlumnosLoading && !cursoAlumnosError && filteredCursoAlumnos.length > 0 && (
                <div className="data-table-wrapper">
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                    <TableExportActions
                      tableSelector="#admin-cursos-alumnos-table"
                      fileName={`curso-alumnos-${formatCourseLabel(selectedCursoForAlumnos || {})}`}
                      title={`Alumnos de ${formatCourseLabel(selectedCursoForAlumnos || {})}`}
                      compact
                      disabled={filteredCursoAlumnos.length === 0}
                    />
                  </div>
                  <table id="admin-cursos-alumnos-table" className="data-table">
                    <thead>
                      <tr>
                        <th>Alumno</th>
                        <th>Email</th>
                        <th className="text-center width-sm">Estado</th>
                        <th className="text-center">Estado académico</th>
                        <th className="text-center width-sm">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCursoAlumnos.map((alumno) => {
                        const alumnoId = alumno.id || alumno.Id;
                        const nombre = alumno.nombre || alumno.Nombre || '';
                        const apellido = alumno.apellido || alumno.Apellido || '';
                        const email = alumno.email || alumno.Email || '—';
                        const displayName = alumno.displayName || alumno.DisplayName || `${nombre} ${apellido}`.trim() || email;
                        const activo = typeof (alumno.activo ?? alumno.Activo) === 'boolean' ? (alumno.activo ?? alumno.Activo) : true;
                        const removing = removingAlumnoIds.includes(alumnoId);

                        return (
                          <tr key={alumnoId}>
                            <td>
                              <div className="prof-name">
                                <span className="name">{displayName}</span>
                                <span className="muted">{[nombre, apellido].filter(Boolean).join(' ') || 'Alumno'}</span>
                              </div>
                            </td>
                            <td>{email}</td>
                            <td className="text-center">
                              <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                                {activo ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td className="text-center">
                              {resumenAcademico.loading ? (
                                <i className="fas fa-spinner fa-spin" />
                              ) : resumenAcademico.porAlumno.get(String(alumnoId)) ? (
                                <EstadoAcademicoChip
                                  estado={resumenAcademico.porAlumno.get(String(alumnoId)).estado}
                                  promedio={resumenAcademico.porAlumno.get(String(alumnoId)).promedio}
                                />
                              ) : (
                                <span title={resumenAcademico.error || undefined}>—</span>
                              )}
                            </td>
                            <td className="text-center">
                              <div className="action-buttons">
                                <button
                                  type="button"
                                  className="action-edit-btn"
                                  onClick={() => setAlumnoNotasDetalle(resumenAcademico.porAlumno.get(String(alumnoId)))}
                                  disabled={!resumenAcademico.porAlumno.get(String(alumnoId))}
                                  title="Ver notas por evaluación"
                                >
                                  <i className="fas fa-clipboard-list" />
                                </button>
                                <button
                                  type="button"
                                  className="action-remove-btn"
                                  onClick={() => handleRemoveAlumnoCurso(alumnoId)}
                                  disabled={removing}
                                  title="Desvincular del curso"
                                >
                                  <i className={`fas ${removing ? 'fa-spinner fa-spin' : 'fa-user-minus'}`} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={closeAlumnosModal}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {removeAllAlumnosModal.open && (
        <div className="modal-overlay" onClick={closeRemoveAllAlumnosModal}>
          <div className="modal-card curso-delete-modal" onClick={(event) => event.stopPropagation()}>
            <div className="curso-delete-modal-header">
              <div className="curso-delete-modal-heading">
                <span className="curso-delete-modal-eyebrow">Desvinculación masiva</span>
                <h3>
                  <i className="fas fa-user-slash" />
                  Desvincular a todos los alumnos del curso
                </h3>
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={closeRemoveAllAlumnosModal}
                disabled={removeAllAlumnosModal.loading}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="curso-delete-modal-body">
              <p className="curso-delete-modal-intro">
                Vas a desasociar a todos los alumnos actualmente vinculados al curso <strong>{formatCourseFullLabel(selectedCursoForAlumnos)}</strong>.
                Esta acción no elimina a los usuarios del sistema ni los quita del colegio: solo remueve su vínculo con este curso puntual.
              </p>

              <div className="curso-delete-summary-grid">
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Curso</span>
                  <strong>{formatCourseLabel(selectedCursoForAlumnos)}</strong>
                </div>
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Ciclo lectivo</span>
                  <strong>{getCursoValue(selectedCursoForAlumnos, 'cicloLectivo') || '—'}</strong>
                </div>
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Alumnos a desvincular</span>
                  <strong>{cursoAlumnos.length}</strong>
                </div>
              </div>

              <div className="curso-delete-impact">
                <p className="curso-delete-impact-title">Qué ocurrirá</p>
                <ul>
                  <li>Se desactivarán todas las asociaciones alumno-curso del curso seleccionado.</li>
                  <li>Los alumnos seguirán existiendo en el sistema y seguirán perteneciendo al colegio.</li>
                  <li>Luego podrás volver a asociarlos manualmente o mediante importación si lo necesitas.</li>
                </ul>
              </div>

              <div className="curso-delete-confirm-box">
                <label htmlFor="curso-remove-all-alumnos-confirm-input">
                  Para confirmar, escribe exactamente:
                </label>
                <code>{removeAllAlumnosExpectedText}</code>
                <input
                  id="curso-remove-all-alumnos-confirm-input"
                  type="text"
                  value={removeAllAlumnosModal.confirmText}
                  onChange={(event) => setRemoveAllAlumnosModal((prev) => ({
                    ...prev,
                    confirmText: event.target.value,
                    error: ''
                  }))}
                  placeholder={removeAllAlumnosExpectedText}
                  autoComplete="off"
                  disabled={removeAllAlumnosModal.loading}
                />
                {removeAllAlumnosModal.confirmText.trim() && (
                  <span className={`curso-delete-confirm-hint ${canConfirmRemoveAllAlumnos ? 'valid' : 'invalid'}`}>
                    {canConfirmRemoveAllAlumnos
                      ? 'Confirmación válida. Ya puedes desvincular a todos los alumnos.'
                      : 'La confirmación no coincide con el texto solicitado.'}
                  </span>
                )}
              </div>

              {removeAllAlumnosModal.error && (
                <div className="curso-delete-error">
                  <i className="fas fa-exclamation-circle" />
                  <span>{removeAllAlumnosModal.error}</span>
                </div>
              )}
            </div>

            <div className="confirm-modal-actions curso-delete-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeRemoveAllAlumnosModal}
                disabled={removeAllAlumnosModal.loading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger curso-delete-confirm-btn"
                onClick={handleRemoveAllAlumnosCurso}
                disabled={!canConfirmRemoveAllAlumnos || removeAllAlumnosModal.loading}
              >
                {removeAllAlumnosModal.loading ? 'Desvinculando...' : 'Desvincular a todos'}
              </button>
            </div>
          </div>
        </div>
      )}

      {profesoresModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '980px', width: 'min(980px, 96vw)' }}>
            <div className="modal-header">
              <h3><i className="fas fa-chalkboard-teacher" /> Profesores del curso</h3>
              <button className="close-btn" onClick={closeProfesoresModal}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <p style={{ margin: 0, color: '#475569' }}>
                  Curso seleccionado: <strong>{selectedCursoForProfesores ? formatCourseLabel(selectedCursoForProfesores) : '—'}</strong>
                </p>
                <button
                  className="refresh-btn"
                  type="button"
                  onClick={() => loadCursoProfesores(selectedCursoForProfesores)}
                  disabled={cursoProfesoresLoading}
                >
                  <i className={`fas ${cursoProfesoresLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
                </button>
              </div>

              <div className="professor-search" style={{ width: '100%', marginBottom: '1rem' }}>
                <i className="fas fa-search" />
                <input
                  type="text"
                  placeholder="Buscar profesor por nombre, email o legajo"
                  value={cursoProfesoresSearch}
                  onChange={(e) => setCursoProfesoresSearch(e.target.value)}
                />
              </div>

              {cursoProfesoresLoading && (
                <div className="data-table-loading">
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin" />
                  </div>
                  <p>Cargando profesores del curso...</p>
                </div>
              )}

              {!cursoProfesoresLoading && cursoProfesoresError && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-exclamation-triangle" />
                  </div>
                  <p>{cursoProfesoresError}</p>
                </div>
              )}

              {!cursoProfesoresLoading && !cursoProfesoresError && cursoProfesores.length === 0 && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-user-slash" />
                  </div>
                  <p>Este curso no tiene profesores asociados.</p>
                </div>
              )}

              {!cursoProfesoresLoading && !cursoProfesoresError && cursoProfesores.length > 0 && filteredCursoProfesores.length === 0 && (
                <div className="data-table-empty">
                  <div className="empty-icon">
                    <i className="fas fa-search" />
                  </div>
                  <p>No hay profesores que coincidan con la búsqueda.</p>
                </div>
              )}

              {!cursoProfesoresLoading && !cursoProfesoresError && filteredCursoProfesores.length > 0 && (
                <div className="data-table-wrapper">
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                    <TableExportActions
                      tableSelector="#admin-cursos-profesores-table"
                      fileName={`curso-profesores-${formatCourseLabel(selectedCursoForProfesores || {})}`}
                      title={`Profesores de ${formatCourseLabel(selectedCursoForProfesores || {})}`}
                      compact
                      disabled={filteredCursoProfesores.length === 0}
                    />
                  </div>
                  <table id="admin-cursos-profesores-table" className="data-table">
                    <thead>
                      <tr>
                        <th>Profesor</th>
                        <th>Email</th>
                        <th>Legajo</th>
                        <th className="text-center width-sm">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCursoProfesores.map((profesor) => {
                        const profesorId = profesor.id || profesor.Id;
                        const nombre = profesor.nombre || profesor.Nombre || '';
                        const apellido = profesor.apellido || profesor.Apellido || '';
                        const email = profesor.email || profesor.Email || '—';
                        const displayName = profesor.displayName || profesor.DisplayName || `${nombre} ${apellido}`.trim() || email;
                        const legajo = profesor.legajo || profesor.Legajo || '—';
                        const activo = typeof (profesor.activo ?? profesor.Activo) === 'boolean' ? (profesor.activo ?? profesor.Activo) : true;

                        return (
                          <tr key={profesorId}>
                            <td>
                              <div className="prof-name">
                                <span className="name">{displayName}</span>
                                <span className="muted">{[nombre, apellido].filter(Boolean).join(' ') || 'Profesor'}</span>
                              </div>
                            </td>
                            <td>{email}</td>
                            <td>{legajo}</td>
                            <td className="text-center">
                              <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                                {activo ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={closeProfesoresModal}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {removeAllMateriasModal.open && (
        <div className="modal-overlay" onClick={closeRemoveAllMateriasModal}>
          <div className="modal-card curso-delete-modal" onClick={(event) => event.stopPropagation()}>
            <div className="curso-delete-modal-header">
              <div className="curso-delete-modal-heading">
                <span className="curso-delete-modal-eyebrow">Desvinculación académica masiva</span>
                <h3>
                  <i className="fas fa-unlink" />
                  Desvincular todas las materias del curso
                </h3>
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={closeRemoveAllMateriasModal}
                disabled={removeAllMateriasModal.loading}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="curso-delete-modal-body">
              <p className="curso-delete-modal-intro">
                Vas a desasociar todas las materias actualmente vinculadas al curso <strong>{formatCourseFullLabel(selectedCursoForMaterias)}</strong>.
                Esta acción no elimina las materias del sistema ni del colegio: solo remueve su vínculo con este curso puntual y desactiva las relaciones académicas derivadas de ese vínculo.
              </p>

              <div className="curso-delete-summary-grid">
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Curso</span>
                  <strong>{formatCourseLabel(selectedCursoForMaterias)}</strong>
                </div>
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Ciclo lectivo</span>
                  <strong>{getCursoValue(selectedCursoForMaterias, 'cicloLectivo') || '—'}</strong>
                </div>
                <div className="curso-delete-summary-card">
                  <span className="curso-delete-summary-label">Materias a desvincular</span>
                  <strong>{cursoMaterias.length}</strong>
                </div>
              </div>

              <div className="curso-delete-impact">
                <p className="curso-delete-impact-title">Qué ocurrirá</p>
                <ul>
                  <li>Se desactivarán todas las asociaciones curso-materia del curso seleccionado.</li>
                  <li>También se desactivarán las relaciones curso-materia-profesor derivadas de esas asociaciones.</li>
                  <li>Las materias seguirán existiendo en el sistema y podrán reutilizarse en una nueva importación o reasignación.</li>
                  <li>Si el Excel vuelve a traer las mismas materias para este curso, se reusarán y se volverán a asociar sin necesidad de recrearlas.</li>
                </ul>
              </div>

              <div className="curso-delete-confirm-box">
                <label htmlFor="curso-remove-all-materias-confirm-input">
                  Para confirmar, escribe exactamente:
                </label>
                <code>{removeAllMateriasExpectedText}</code>
                <input
                  id="curso-remove-all-materias-confirm-input"
                  type="text"
                  value={removeAllMateriasModal.confirmText}
                  onChange={(event) => setRemoveAllMateriasModal((prev) => ({
                    ...prev,
                    confirmText: event.target.value,
                    error: ''
                  }))}
                  placeholder={removeAllMateriasExpectedText}
                  autoComplete="off"
                  disabled={removeAllMateriasModal.loading}
                />
                {removeAllMateriasModal.confirmText.trim() && (
                  <span className={`curso-delete-confirm-hint ${canConfirmRemoveAllMaterias ? 'valid' : 'invalid'}`}>
                    {canConfirmRemoveAllMaterias
                      ? 'Confirmación válida. Ya puedes desvincular todas las materias.'
                      : 'La confirmación no coincide con el texto solicitado.'}
                  </span>
                )}
              </div>

              {removeAllMateriasModal.error && (
                <div className="curso-delete-error">
                  <i className="fas fa-exclamation-circle" />
                  <span>{removeAllMateriasModal.error}</span>
                </div>
              )}
            </div>

            <div className="confirm-modal-actions curso-delete-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeRemoveAllMateriasModal}
                disabled={removeAllMateriasModal.loading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger curso-delete-confirm-btn"
                onClick={handleRemoveAllMateriasCurso}
                disabled={!canConfirmRemoveAllMaterias || removeAllMateriasModal.loading}
              >
                {removeAllMateriasModal.loading ? 'Desvinculando...' : 'Desvincular todas las materias'}
              </button>
            </div>
          </div>
        </div>
      )}
      {evaluacionesCurso && evaluacionesApi && (
        <EvaluacionesCursoModal
          api={evaluacionesApi}
          cursoNombre={formatCourseLabel(evaluacionesCurso.curso)}
          initialView={evaluacionesCurso.initialView}
          onClose={() => setEvaluacionesCurso(null)}
          onChanged={resumenAcademico.reload}
        />
      )}
      {alumnoNotasDetalle && (
        <AlumnoNotasDetalle
          alumno={alumnoNotasDetalle}
          evaluaciones={resumenAcademico.resumen?.evaluaciones || []}
          onClose={() => setAlumnoNotasDetalle(null)}
        />
      )}
    </div>
  );
};

export default AdminCursosSection;
